import { ReactComponent as BitbucketIcon } from '@elementary/common-ui/assets/16x16/bitbucket.svg';
import { ReactComponent as GitHubIcon } from '@elementary/common-ui/assets/16x16/github.svg';
import { ReactComponent as GitLabIcon } from '@elementary/common-ui/assets/16x16/gitlab.svg';
import {
  FlexColumn,
  TextField,
  PasswordField,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import FormControls from '@elementary/common-ui/components/FormControls';
import {
  Collapse,
  ToggleButtonGroup,
  Typography,
  Divider,
} from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { VersionControl, LookerForm as LookerFormFields } from 'shared/types';
import { fontWeight } from '@elementary/common-ui/style/utils';
import DialogToggleButton from '../../../shared/DialogToggleButton';
import AuthSection from './AuthSection';

const LookerForm = ({
  formMethods,
  selectedGitProvider,
  onGitProviderChange,
}: {
  formMethods: UseFormReturn<LookerFormFields, any, undefined>;
  selectedGitProvider: VersionControl | null;
  onGitProviderChange: (val: VersionControl | null) => void;
}) => {
  const {
    register,
    formState: { errors, dirtyFields },
    setValue,
  } = formMethods;

  return (
    <>
      <FormControls.InputWrapper>
        <TextField
          {...register('lookerConnectionName', {
            required: requiredLabel,
          })}
          variant="outlined"
          label="Connection name"
          size="large"
        />
        <FormControls.ErrorMessage>
          {errors.lookerConnectionName?.message}
        </FormControls.ErrorMessage>
      </FormControls.InputWrapper>

      <FormControls.InputWrapper>
        <TextField
          {...register('lookerServerUrl', {
            required: requiredLabel,
          })}
          variant="outlined"
          label="Host/Instance URL"
          size="large"
        />
        <FormControls.ErrorMessage>
          {errors.lookerServerUrl?.message}
        </FormControls.ErrorMessage>
      </FormControls.InputWrapper>
      <FormControls.InputWrapper>
        <TextField
          {...register('lookerClientId', {
            required: requiredLabel,
          })}
          variant="outlined"
          label="Client ID"
          size="large"
        />
        <FormControls.ErrorMessage>
          {errors.lookerClientId?.message}
        </FormControls.ErrorMessage>
      </FormControls.InputWrapper>
      <FormControls.InputWrapper>
        <PasswordField
          {...register('lookerClientSecret', {
            required: requiredLabel,
          })}
          label="Client secret"
          isFormValueDirty={dirtyFields.lookerClientSecret}
          clearFormValue={() => setValue('lookerClientSecret', '')}
          size="large"
        />
        <FormControls.ErrorMessage>
          {errors.lookerClientSecret?.message}
        </FormControls.ErrorMessage>
      </FormControls.InputWrapper>

      <FlexColumn sx={{ gap: 1 }}>
        <Typography>Select your LookML code repository</Typography>

        <ToggleButtonGroup
          fullWidth
          exclusive
          value={selectedGitProvider}
          onChange={(_, val) => {
            const value = val || selectedGitProvider;
            onGitProviderChange(value);
            setValue('lookerGitProvider', value);
          }}
        >
          <DialogToggleButton value="GitHub">
            <GitHubIcon />
            GitHub
          </DialogToggleButton>
          <DialogToggleButton value="GitLab">
            <GitLabIcon />
            GitLab
          </DialogToggleButton>
          <DialogToggleButton value="Bitbucket">
            <BitbucketIcon />
            Bitbucket
          </DialogToggleButton>
        </ToggleButtonGroup>
      </FlexColumn>

      <Collapse in={!!selectedGitProvider}>
        <FormControls.FieldsWrapper>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: fontWeight('medium') }}
          >
            Authentication
          </Typography>
          <AuthSection
            selectedGitProvider={selectedGitProvider}
            formMethods={formMethods}
            requiredLabel={requiredLabel}
          />
        </FormControls.FieldsWrapper>

        <Divider sx={{ my: 3 }} />

        <FormControls.FieldsWrapper>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: fontWeight('medium') }}
          >
            Repository Details
          </Typography>
          <FormControls.InputWrapper>
            <TextField
              {...register('lookerRepoName', {
                required: requiredLabel,
              })}
              variant="outlined"
              label="Repository name"
              placeholder="organization-name/repository-name"
              size="large"
            />
            <FormControls.ErrorMessage>
              {errors.lookerRepoName?.message}
            </FormControls.ErrorMessage>
          </FormControls.InputWrapper>
        </FormControls.FieldsWrapper>
      </Collapse>
    </>
  );
};

export default LookerForm;
